import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiConfiguration } from '@/core/api/api-configuration';
import { BaseService } from '@/core/api/base-service';
import { environment } from '@env/environment';
import { SICOM_PATHS } from 'src/sicom-paths';
import { Page } from '@/shared/models/page.model';
import { MedicoCadastroGeralResponse } from '@/core/api/sicom-api/registro/medico/controle-administrativo/medico-cadastro-geral.model';
import { PrestadorEstatisticaAgrupadaResponse, PrestadorEstatisticaCadastroAnaliticoResponse } from './prestador-estatistica-agrupada.model';

@Injectable({ providedIn: 'root' })
export class PrestadorControleAdministrativoService extends BaseService {
    constructor(config: ApiConfiguration, http: HttpClient) {
        super(config, http, environment.apis.apiSicom);
    }

    readonly PATH = SICOM_PATHS.registro.prestador.controleAdministrativo;

    consultarPrestadoresPorSituacao(uf?: string, limite?: number, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/situacoes`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarAtivosPorRegistroCadastro(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/ativos-registro-cadastro`, this.criarOpcoes(uf, context));
    }

    consultarNovasInscricoesPorMes(uf?: string, ano?: number, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/novas-inscricoes-por-mes`, this.criarOpcoes(uf, context, { ano }));
    }

    consultarPrestadoresPorMunicipio(uf?: string, limite?: number, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/municipios`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarPrestadoresPorTipoEstabelecimento(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/tipos-estabelecimento`, this.criarOpcoes(uf, context));
    }

    consultarPrestadoresPorCategoria(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/categorias`, this.criarOpcoes(uf, context));
    }

    consultarPrestadoresPorNatureza(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/naturezas`, this.criarOpcoes(uf, context));
    }

    consultarEnderecosCorrespondenciaAusentes(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/enderecos-ausentes`, this.criarOpcoes(uf, context));
    }

    consultarCertificadosPorSituacao(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/certificados-situacao`, this.criarOpcoes(uf, context));
    }

    consultarCertificadosPorFaixaVencimento(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/certificados-vencimento-faixa`, this.criarOpcoes(uf, context));
    }

    consultarRenovacoesCertificadoPorMes(uf?: string, ano?: number, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/renovacoes-certificado-por-mes`, this.criarOpcoes(uf, context, { ano }));
    }

    consultarTempoMedioRenovacaoCertificado(uf?: string, ano?: number, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/tempo-medio-renovacao-certificado`, this.criarOpcoes(uf, context, { ano }));
    }

    consultarPrestadoresSemDiretorTecnico(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/sem-diretor-tecnico`, this.criarOpcoes(uf, context));
    }

    consultarDiretoresTecnicosPorVinculos(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/diretores-tecnicos-vinculos`, this.criarOpcoes(uf, context));
    }

    consultarDiretoriaDuplicada(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/diretoria-duplicada`, this.criarOpcoes(uf, context));
    }

    consultarPrestadoresPorTamanhoCorpoClinico(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/corpo-clinico-tamanho`, this.criarOpcoes(uf, context));
    }

    consultarCorpoClinicoPorNaturezaVinculo(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/corpo-clinico-vinculo`, this.criarOpcoes(uf, context));
    }

    consultarPrestadoresPorComissaoEticaVigente(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/comissao-etica-vigente`, this.criarOpcoes(uf, context));
    }

    consultarComissaoEticaPorVencimento(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/comissao-etica-vencimento`, this.criarOpcoes(uf, context));
    }

    consultarComissaoEticaObrigatoriaAusente(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/comissao-etica-obrigatoria-ausente`, this.criarOpcoes(uf, context));
    }

    consultarComissoesObrigatoriasPorTipo(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/comissoes-obrigatorias-tipo`, this.criarOpcoes(uf, context));
    }

    consultarCnaesPrincipais(uf?: string, limite?: number, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/cnaes-principais`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarPrestadoresSemCnaePrincipal(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/sem-cnae-principal`, this.criarOpcoes(uf, context));
    }

    consultarCnaesSecundariosPorPrestador(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/cnaes-secundarios`, this.criarOpcoes(uf, context));
    }

    consultarServicosMaisDeclarados(uf?: string, limite?: number, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/servicos`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarAtividadesDesenvolvidas(uf?: string, limite?: number, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/atividades`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarRecursosHumanosPorTipo(uf?: string, limite?: number, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/recursos-humanos`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarRecursosMateriaisPorTipo(uf?: string, limite?: number, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/recursos-materiais`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarEspecialidadesMaisRegistradas(uf?: string, limite?: number, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/especialidades`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarPrestadoresSemEspecialidadeObrigatoria(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/sem-especialidade-obrigatoria`, this.criarOpcoes(uf, context));
    }

    consultarPendenciasManuaisPorTipo(uf?: string, limite?: number, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/pendencias-manuais`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarPendenciasOperacionaisPorTipo(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/pendencias-operacionais`, this.criarOpcoes(uf, context));
    }

    consultarDuplicidadesCnpj(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/cnpj-duplicado`, this.criarOpcoes(uf, context));
    }

    consultarRazaoMedicosPrestadores(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/razao-medicos-prestadores`, this.criarOpcoes(uf, context));
    }

    consultarMultivinculoCorpoClinico(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/multivinculo-corpo-clinico`, this.criarOpcoes(uf, context));
    }

    consultarDiretoresTecnicosPorFaixaEtaria(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/diretores-tecnicos-faixa-etaria`, this.criarOpcoes(uf, context));
    }

    consultarCicloVidaPrestador(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/ciclo-vida-prestador`, this.criarOpcoes(uf, context));
    }

    consultarCapacidadeInstaladaPorUf(uf?: string, limite?: number, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/capacidade-instalada-uf`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarPrestadoresPorFaixaCapitalSocial(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/faixa-capital-social`, this.criarOpcoes(uf, context));
    }

    consultarEnderecosCorrespondenciaDesatualizados(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/enderecos-desatualizados`, this.criarOpcoes(uf, context));
    }

    consultarVinculadosEmDebito(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/vinculados-debito`, this.criarOpcoes(uf, context));
    }

    consultarPrestadoresPorDelegacia(uf?: string, limite?: number, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/delegacias`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarEleitoradoPorCorpoClinico(uf?: string, limite?: number, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/eleitorado-corpo-clinico`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarCadastroGeralPrestador(context?: HttpContext): Observable<MedicoCadastroGeralResponse[]> {
        return this.http.get<MedicoCadastroGeralResponse[]>(`${this.PATH}/consulta/cadastro-geral`, this.criarOpcoes(undefined, context));
    }

    consultarTotalPrestadoresAtivosNaData(uf: string | undefined, data: string, context?: HttpContext): Observable<number> {
        return this.http.get<number>(`${this.PATH}/consulta/ativos-na-data/total`, this.criarOpcoes(uf, context, { data }));
    }

    consultarPrestadoresAtivosNaData(uf: string | undefined, data: string, page?: number, size?: number, context?: HttpContext): Observable<Page<PrestadorEstatisticaCadastroAnaliticoResponse>> {
        return this.http.get<Page<PrestadorEstatisticaCadastroAnaliticoResponse>>(`${this.PATH}/consulta/ativos-na-data`, this.criarOpcoes(uf, context, { data, page, size }));
    }

    consultarSociosPorFaixa(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/socios-faixa`, this.criarOpcoes(uf, context));
    }

    consultarSociosPorCargo(uf?: string, limite?: number, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/socios-cargo`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarFiliaisPorLocalizacaoMatriz(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/filiais-matriz`, this.criarOpcoes(uf, context));
    }

    consultarPrestadoresSemCnes(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/sem-cnes`, this.criarOpcoes(uf, context));
    }

    consultarMovimentacaoSituacaoPjPorMes(uf?: string, ano?: number, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/movimentacao-situacao-pj`, this.criarOpcoes(uf, context, { ano }));
    }

    consultarPreCadastrosPrestadorPorUf(uf?: string, limite?: number, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/pre-cadastros`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarChefiasPorServico(uf?: string, limite?: number, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/chefias-servico`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarServicosSemChefia(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/servicos-sem-chefia`, this.criarOpcoes(uf, context));
    }

    consultarPrestadoresPorDocumentoConstituicao(uf?: string, limite?: number, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/documento-constituicao`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarPrestadoresPorEnsino(uf?: string, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/ensino`, this.criarOpcoes(uf, context));
    }

    consultarPrestadoresPorGestao(uf?: string, limite?: number, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/gestao`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarPrestadoresPorAbrangencia(uf?: string, limite?: number, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/abrangencia`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarCorpoClinicoEntradasPorMes(uf?: string, ano?: number, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/corpo-clinico-entradas`, this.criarOpcoes(uf, context, { ano }));
    }

    consultarCorpoClinicoSaidasPorMes(uf?: string, ano?: number, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/corpo-clinico-saidas`, this.criarOpcoes(uf, context, { ano }));
    }

    consultarAnotacoesPrestadorPorTipo(uf?: string, limite?: number, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/anotacoes-prestador-tipo`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarAnotacoesPrestadorPorMes(uf?: string, ano?: number, context?: HttpContext): Observable<PrestadorEstatisticaAgrupadaResponse[]> {
        return this.http.get<PrestadorEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/anotacoes-prestador-mes`, this.criarOpcoes(uf, context, { ano }));
    }

    consultarPrestadoresEstatisticaCadastro(estatisticaId: string, uf?: string | null, ano?: number, page?: number, size?: number, codigo?: string | null, context?: HttpContext): Observable<Page<PrestadorEstatisticaCadastroAnaliticoResponse>> {
        return this.http.get<Page<PrestadorEstatisticaCadastroAnaliticoResponse>>(`${this.PATH}/consulta/estatisticas-cadastro/${estatisticaId}/prestadores`, this.criarOpcoes(uf ?? undefined, context, { ano, page, size, codigo }));
    }

    private criarOpcoes(uf?: string, context?: HttpContext, parametrosExtras: Record<string, string | number | null | undefined> = {}): { params?: HttpParams; context?: HttpContext } {
        let params = new HttpParams();

        if (uf) {
            params = params.set('uf', uf);
        }

        Object.entries(parametrosExtras).forEach(([chave, valor]) => {
            if (valor !== null && valor !== undefined && valor !== '') {
                params = params.set(chave, String(valor));
            }
        });

        if (!params.keys().length) {
            return { context };
        }

        return { params, context };
    }
}
